import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="text-base sm:text-lg font-bold text-[#1800ad] mb-2">{n}. {title}</h2>
    {children}
  </section>
);

const SubHeading = ({ children }) => (
  <h3 className="font-bold text-[#ff8500] mt-3 mb-1 text-sm">{children}</h3>
);

const TermsPage = () => {
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #f0edff 0%, #fff5f0 50%, #f0edff 100%)' }}>
      {/* Header Bar — solid blue, compact height */}
      <div className="bg-[#1800ad] py-5 sm:py-7 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 80% 50%, #ffffff 0%, transparent 50%)' }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-xs sm:text-sm font-medium mb-2 sm:mb-3 transition-colors" data-testid="back-link">
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <div className="flex items-center gap-2.5">
            <FileText size={22} className="text-white sm:w-6 sm:h-6" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white" data-testid="terms-title">Terms &amp; Conditions</h1>
          </div>
          <p className="text-white/70 text-[11px] sm:text-xs mt-1.5">Last Updated: June 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-8 -mt-4 sm:-mt-6">
        <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 lg:p-12 shadow-lg border border-[#1800ad]/5" data-testid="terms-content">
          <div className="space-y-5 sm:space-y-7 text-xs sm:text-sm text-[#4B5563] leading-relaxed">

            <Section n={1} title="Acceptance of Terms">
              <p>Welcome to GenzoX (&quot;Platform&quot;, &quot;GenzoX&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;).</p>
              <p className="mt-2">By accessing, browsing, registering, posting listings, purchasing plans, or otherwise using GenzoX, you agree to be legally bound by these Terms &amp; Conditions. If you do not agree to these Terms, you must discontinue use of the Platform immediately.</p>
              <p className="mt-2">These Terms apply to all users, including buyers, sellers, dealers, brokers, agents, businesses, advertisers, and visitors.</p>
            </Section>

            <Section n={2} title="Platform Role (Intermediary Status)">
              <p>GenzoX operates as a technology platform and online marketplace that enables users to create listings and connect with other users.</p>
              <p className="mt-2 font-semibold">GenzoX:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Does not own listed products or services</li>
                <li>Does not inspect, verify, certify, or guarantee listings</li>
                <li>Does not act as buyer or seller</li>
                <li>Does not participate in transactions between users</li>
                <li>Does not guarantee payment, delivery, ownership, authenticity, condition, legality, or performance of any item or service</li>
              </ul>
              <p className="mt-2">All transactions are conducted solely between users.</p>
              <p className="mt-2">GenzoX functions as an intermediary in accordance with applicable Indian laws and intermediary guidelines.</p>
            </Section>

            <Section n={3} title="User Eligibility">
              <p>To use GenzoX, you must:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Be at least 18 years of age</li>
                <li>Provide accurate information</li>
                <li>Have the legal capacity to enter into binding agreements</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
              <p className="mt-2">GenzoX reserves the right to suspend or terminate accounts that provide false or misleading information.</p>
            </Section>

            <Section n={4} title="Account Registration">
              <p>Users may register using:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Mobile Number OTP</li>
                <li>Google Sign-In</li>
                <li>Other authentication methods made available by GenzoX</li>
              </ul>
              <p className="mt-2 font-semibold">Users are responsible for:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Maintaining account security</li>
                <li>Protecting login credentials</li>
                <li>Activities conducted through their account</li>
              </ul>
              <p className="mt-2 font-semibold">Users must not:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Share accounts</li>
                <li>Transfer accounts</li>
                <li>Create fraudulent accounts</li>
                <li>Create duplicate accounts for abuse or manipulation</li>
              </ul>
            </Section>

            <Section n={5} title="Mobile Verification Requirement">
              <p>Certain platform activities, including posting advertisements, may require mobile number verification.</p>
              <p className="mt-2 font-semibold">GenzoX reserves the right to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Require OTP verification</li>
                <li>Restrict posting privileges</li>
                <li>Suspend unverified accounts</li>
              </ul>
              <p className="mt-2">Verification does not constitute identity verification or endorsement by GenzoX.</p>
            </Section>

            <Section n={6} title="User Responsibilities">
              <p>Users agree not to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Post false information</li>
                <li>Misrepresent products or services</li>
                <li>Engage in fraud</li>
                <li>Post illegal content</li>
                <li>Violate intellectual property rights</li>
                <li>Harass other users</li>
                <li>Send spam messages</li>
                <li>Manipulate search rankings</li>
                <li>Create duplicate listings</li>
                <li>Circumvent platform policies</li>
              </ul>
              <p className="mt-2 font-semibold">Violation may result in:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Listing removal</li>
                <li>Account suspension</li>
                <li>Account termination</li>
                <li>Reporting to authorities where applicable</li>
              </ul>
            </Section>

            <Section n={7} title="Listings & Content">
              <p>Users remain solely responsible for:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Listing content</li>
                <li>Descriptions</li>
                <li>Images</li>
                <li>Prices</li>
                <li>Locations</li>
                <li>Ownership claims</li>
              </ul>
              <p className="mt-2 font-semibold">GenzoX does not verify:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Accuracy</li>
                <li>Authenticity</li>
                <li>Ownership</li>
                <li>Legal status</li>
              </ul>
              <p className="mt-2 font-semibold">GenzoX may remove listings that:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Violate policies</li>
                <li>Appear fraudulent</li>
                <li>Are misleading</li>
                <li>Are duplicates</li>
                <li>Breach applicable laws</li>
              </ul>
            </Section>

            <Section n={8} title="Prohibited & Restricted Listings">
              <p>GenzoX is a classified marketplace intended for legitimate buying, selling and exchange of permitted goods and services. To maintain a safe, appropriate and trustworthy marketplace, users must not post, promote, or offer listings that fall outside our permitted categories or violate applicable laws or GenzoX platform guidelines.</p>
              <p className="mt-2 font-semibold">The following types of listings are not permitted on GenzoX:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Adult and sexually explicit products or services, including sex toys, sexual stimulation products and similar adult-only merchandise.</li>
                <li>Pornographic, sexually explicit or sexually suggestive content, images or advertisements.</li>
                <li>Escort, prostitution, sexual services or arrangements of a sexual nature.</li>
                <li>Listings promoting or facilitating illegal activities or unlawful services.</li>
                <li>Drugs, narcotics, controlled substances or other prohibited substances.</li>
                <li>Weapons, firearms, ammunition, explosives or other prohibited weapons.</li>
                <li>Counterfeit, stolen or illegally obtained goods.</li>
                <li>Listings containing fraudulent, misleading or deceptive information.</li>
                <li>Listings containing obscene, offensive, abusive or otherwise inappropriate content.</li>
                <li>Products or services that do not fall within the categories or subcategories supported by GenzoX.</li>
                <li>Any other product, service or content that GenzoX reasonably determines to be unsuitable for the platform or inconsistent with its marketplace guidelines.</li>
              </ul>

              <SubHeading>Listing Review &amp; Removal</SubHeading>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>GenzoX reserves the right to review, reject, restrict, suspend or remove any listing that violates these guidelines or is otherwise unsuitable for the platform.</li>
                <li>GenzoX may use automated systems, AI-assisted moderation and manual administrative review to identify potentially prohibited, inappropriate or non-compliant listings.</li>
                <li>Where appropriate, users may be informed of the general reason why a listing was rejected or removed. GenzoX may also request clarification or additional information from the user.</li>
              </ul>

              <div className="mt-3 rounded-xl border border-[#ff8500]/40 bg-[#fff4ed] p-3 sm:p-4" data-testid="paid-plans-no-guarantee">
                <p className="font-bold text-[#ff8500] text-sm mb-1">Paid Plans Do Not Guarantee Listing Approval</p>
                <p>Purchasing a GenzoX subscription, listing plan, Pro Listing, Boost, Dealer Plan or any other paid service does not guarantee approval, publication or continued availability of an advertisement. All listings remain subject to GenzoX&apos;s content, category, eligibility and moderation guidelines.</p>
                <p className="mt-2">A listing may therefore be rejected or removed even when the user has an active paid plan or has purchased a paid listing/add-on service.</p>
              </div>
            </Section>


            <Section n={9} title="Categories & Dealer Plans">
              <p>GenzoX may offer dealer-specific plans for eligible categories including but not limited to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Mobiles</li>
                <li>Bikes</li>
                <li>Cars</li>
                <li>Property</li>
                <li>Other categories introduced in future</li>
              </ul>
              <p className="mt-2 font-semibold">Dealer plans are intended for:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Businesses</li>
                <li>Dealers</li>
                <li>Brokers</li>
                <li>Agencies</li>
                <li>Commercial sellers</li>
              </ul>
              <p className="mt-2 font-semibold">GenzoX reserves the right to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Request business information</li>
                <li>Reclassify accounts</li>
                <li>Suspend dealer benefits for misuse</li>
              </ul>
              <p className="mt-2">Dealer plan purchase does not guarantee sales, leads, visibility, or customer engagement.</p>
            </Section>

            <Section n={10} title="Subscription Plans & Paid Services">
              <p>GenzoX may offer:</p>
              <SubHeading>Regular Plans</SubHeading>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Free Plans</li>
                <li>Starter Plans</li>
                <li>Subscription Plans</li>
              </ul>
              <SubHeading>Dealer Plans</SubHeading>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Category-specific dealer plans</li>
                <li>Property dealer plans</li>
              </ul>
              <SubHeading>Add-On Services</SubHeading>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Pro Listing</li>
                <li>Boost Entry</li>
                <li>Boost Standard</li>
                <li>Additional promotional products</li>
              </ul>
              <p className="mt-2">Plan pricing, validity, features, and benefits may change without prior notice.</p>
              <p className="mt-2">All purchases are subject to applicable taxes and payment gateway fees where applicable.</p>
            </Section>

            <Section n={11} title="Plan Expiry">
              <p>Listings may expire upon:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Plan expiry</li>
                <li>Ad limit exhaustion</li>
                <li>Account restrictions</li>
              </ul>
              <p className="mt-2 font-semibold">Expired listings may:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Become inactive</li>
                <li>Be archived</li>
                <li>Become ineligible for public display</li>
              </ul>
              <p className="mt-2">GenzoX may notify users regarding plan expiration, but users remain responsible for tracking plan validity.</p>
            </Section>

            <Section n={12} title="Ad Restoration">
              <p>GenzoX may provide restoration options for eligible expired listings.</p>
              <p className="mt-2 font-semibold">Restoration eligibility may depend upon:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Account status</li>
                <li>Plan eligibility</li>
                <li>Retention periods</li>
                <li>Image archive availability</li>
              </ul>
              <p className="mt-2">Restoration does not guarantee preservation of all content if retention periods have elapsed.</p>
            </Section>

            <Section n={13} title="Image Archiving & Purging">
              <p>Images associated with listings may be:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Stored</li>
                <li>Archived</li>
                <li>Compressed</li>
                <li>Watermarked</li>
                <li>Restored</li>
                <li>Permanently deleted</li>
              </ul>
              <p className="mt-2">After expiry, images may enter an archive period.</p>
              <p className="mt-2">After the archive period ends, images may be permanently purged.</p>
              <p className="mt-2">Textual listing information may be retained separately from images.</p>
              <p className="mt-2">GenzoX does not guarantee indefinite retention of uploaded media.</p>
            </Section>

            <Section n={14} title="Search Rankings & Promotional Visibility">
              <p>Search results, homepage feeds, category pages, and recommendation sections may be influenced by:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Search relevance</li>
                <li>Listing freshness</li>
                <li>Location relevance</li>
                <li>User activity</li>
                <li>Category relevance</li>
                <li>Pro Listings</li>
                <li>Boost Entry</li>
                <li>Boost Standard</li>
                <li>Promotional products</li>
              </ul>
              <p className="mt-2">Purchasing promotional services may improve visibility but does not guarantee inquiries, sales, or conversions.</p>
            </Section>

            <Section n={15} title="Messaging & Chat Services">
              <p>GenzoX may provide messaging functionality.</p>
              <p className="mt-2 font-semibold">Users agree not to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Send spam</li>
                <li>Harass users</li>
                <li>Share fraudulent information</li>
                <li>Conduct illegal activities</li>
              </ul>
              <p className="mt-2">GenzoX may monitor, investigate, restrict, or suspend messaging privileges to prevent abuse or fraud.</p>
            </Section>

            <Section n={16} title="Location Services & Maps">
              <p>GenzoX may use:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>GPS</li>
                <li>Device location</li>
                <li>PIN codes</li>
                <li>Maps</li>
                <li>User-entered locations</li>
              </ul>
              <p className="mt-2">Location information may be approximate.</p>
              <p className="mt-2">Users remain responsible for verifying locations and meeting locations independently.</p>
              <p className="mt-2">GenzoX does not guarantee location accuracy.</p>
            </Section>

            <Section n={17} title="Payments">
              <p>Payments may be processed through third-party providers.</p>
              <p className="mt-2 font-semibold">GenzoX:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Does not store complete payment card information</li>
                <li>Is not responsible for payment processor failures</li>
                <li>Is not liable for banking or payment gateway issues</li>
              </ul>
              <p className="mt-2">Users must comply with the terms of payment providers.</p>
            </Section>

            <Section n={18} title="Refund Policy">
              <p>Unless required by law or explicitly stated:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Subscription plans are non-refundable</li>
                <li>Dealer plans are non-refundable</li>
                <li>Promotional products are non-refundable</li>
                <li>Add-on purchases are non-refundable</li>
              </ul>
              <p className="mt-2">GenzoX may issue refunds at its sole discretion.</p>
              <p className="mt-2"><strong>Voluntary account deletion:</strong> If you choose to delete your account while one or more paid plans, dealer plans, promotional products, or add-on entitlements are still active, the remaining period and unused entitlements will be forfeited at the moment of deletion and are non-refundable, unless otherwise required by applicable law.</p>
            </Section>

            <Section n={19} title="No Warranty">
              <p>The Platform is provided on an &quot;As Is&quot; and &quot;As Available&quot; basis.</p>
              <p className="mt-2 font-semibold">GenzoX makes no warranties regarding:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Availability</li>
                <li>Accuracy</li>
                <li>Reliability</li>
                <li>User conduct</li>
                <li>Product quality</li>
                <li>Service quality</li>
                <li>Transaction outcomes</li>
              </ul>
            </Section>

            <Section n={20} title="Limitation of Liability">
              <p>To the fullest extent permitted by law, GenzoX shall not be liable for:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Direct losses</li>
                <li>Indirect losses</li>
                <li>Lost profits</li>
                <li>Fraudulent transactions</li>
                <li>User disputes</li>
                <li>Data loss</li>
                <li>Listing loss</li>
                <li>Image loss</li>
                <li>Business interruption</li>
              </ul>
            </Section>

            <Section n={21} title="Fraud Prevention & Moderation">
              <p>GenzoX may use:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Manual moderation</li>
                <li>Automated moderation</li>
                <li>AI moderation</li>
                <li>Duplicate detection systems</li>
                <li>Fraud detection systems</li>
              </ul>
              <p className="mt-2">GenzoX reserves the right to remove listings or restrict accounts deemed suspicious.</p>
            </Section>

            <Section n={22} title="Law Enforcement Cooperation">
              <p>GenzoX may disclose information when:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Required by law</li>
                <li>Requested through lawful process</li>
                <li>Necessary to protect rights or safety</li>
              </ul>
              <p className="mt-2">Requests must be made through authorized legal channels.</p>
            </Section>

            <Section n={23} title="Suspension & Termination">
              <p>GenzoX may:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Suspend accounts</li>
                <li>Terminate accounts</li>
                <li>Remove listings</li>
                <li>Restrict services</li>
              </ul>
              <p className="mt-2 font-semibold">For:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Policy violations</li>
                <li>Fraud</li>
                <li>Abuse</li>
                <li>Security concerns</li>
                <li>Legal compliance</li>
              </ul>

              <SubHeading>22.1 User-Initiated Account Deletion</SubHeading>
              <p>You may permanently delete your GenzoX account at any time, directly from within the app or website, without contacting customer support.</p>
              <p className="mt-2"><strong>Path:</strong> Sign in &rarr; Profile &rarr; My Account &rarr; <em>Delete My Account</em> &rarr; review the consequences screen &rarr; type <strong>DELETE</strong> to confirm &rarr; tap <em>Delete Account</em>.</p>

              <p className="mt-2 font-semibold">By initiating account deletion, you acknowledge and agree that:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>The deletion is <strong>permanent and irreversible</strong> &mdash; we cannot restore the account once the deletion is processed.</li>
                <li>All active and inactive <strong>listings</strong> belonging to your account will be removed and will no longer appear in search, recommendations, or any public area of the Platform.</li>
                <li>Your <strong>chat history</strong>, conversations, wishlist, saved searches, and reviews authored by you will be deleted.</li>
                <li>Any active <strong>subscription plans, dealer plans, promotional products, and add-on entitlements</strong> will be cancelled at the moment of deletion. Remaining unused periods are <strong>non-refundable</strong> (see Section 18 &mdash; Refund Policy).</li>
                <li>You will be <strong>immediately signed out</strong> on every device on which you are logged in.</li>
                <li>Certain records may be <strong>retained in anonymized form</strong> where required by law or for legitimate business purposes, including payment and invoice records (Indian Income Tax Act, GST Act, Companies Act), administrative audit logs, abuse-report context, and moderation history. Such retained records are stripped of personal identifiers. Refer to the <a href="/privacy" className="text-[#1800ad] font-semibold hover:underline">Privacy Policy</a> Section 10.1 for details.</li>
                <li>You may create a new GenzoX account at any time in the future. A new account will not inherit any data, history, listings, reviews, ratings, plans, or entitlements from the deleted account.</li>
              </ul>

              <p className="mt-2"><strong>Alternative path:</strong> If you have lost access to your registered phone number or email and cannot sign in to initiate deletion from within the app, you may email <a href="mailto:help@genzox.in" className="text-[#1800ad] font-semibold hover:underline">help@genzox.in</a> from your registered email address. We will verify your identity and complete the deletion within <strong>30 days</strong> of receiving a verifiable request.</p>
            </Section>

            <Section n={24} title="Intellectual Property">
              <p>GenzoX trademarks, logos, branding, software, and content remain the property of GenzoX unless otherwise stated.</p>
              <p className="mt-2">Users may not reproduce or distribute platform content without authorization.</p>
            </Section>

            <Section n={25} title="Changes to Terms">
              <p>GenzoX may modify these Terms at any time.</p>
              <p className="mt-2">Continued use of the Platform after updates constitutes acceptance of revised Terms.</p>
            </Section>

            <Section n={26} title="Governing Law">
              <p>These Terms shall be governed by the laws of India.</p>
              <p className="mt-2">Any disputes shall be subject to the jurisdiction of competent courts in India.</p>
            </Section>

            <Section n={27} title="Contact & Grievance Officer">
              <p>For support, complaints, legal requests, privacy concerns, or grievance submissions, users may contact:</p>
              <p className="mt-2">Email: <a href="mailto:help@genzox.in" className="text-[#1800ad] font-semibold hover:text-[#ff8500]">help@genzox.in</a></p>
              <p className="mt-2">GenzoX will process complaints and grievances in accordance with applicable Indian laws and regulations.</p>
            </Section>

          </div>
        </div>

        {/* Footer spacer */}
        <div className="py-8 text-center">
          <Link to="/privacy" className="text-[#1800ad] hover:text-[#ff8500] text-sm font-semibold transition-colors" data-testid="go-to-privacy">
            Read our Privacy Policy &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
